import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  VerticalContainer,
  VerticalResizableSectionContainer,
  VerticalResizer,
} from './atoms';
import { getMouseDownHandler } from './getMouseDownHandler';
import {
  Direction,
  ResizeMouseEventHandler,
  State,
  VerticalResizable,
} from './types';
import { reduceIsCollapsed } from './reduceIsCollapsed';
import {
  getContainerState,
  getVerticalConfigFromChild,
  haveChildrenDimensionsConfigPropsChangedVertical,
  toInitialVerticalStyle,
  updateConfigs,
} from './utils';
import { reduceIsCollapsedAllChildren } from './reduceIsCollapsedAllChildren';

type ResizableVerticalProps = {
  children: ReactElement<Record<string, any> & VerticalResizable>[];
  isPropagate?: boolean;
  onResizeEnd?: ResizeMouseEventHandler;
  onResizeStart?: ResizeMouseEventHandler;
};

const ResizableVertical = ({
  children,
  isPropagate = true,
  onResizeEnd,
  onResizeStart,
}: ResizableVerticalProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [
    {
      activeResizerIndex,
      styles,
      configs,
      resizerStyles,
      isResizeEndScheduled,
    },
    setState,
  ] = useState<State>({
    startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
    configs: children.map(getVerticalConfigFromChild),
    activeResizerIndex: -1,
    styles: children.map(toInitialVerticalStyle),
    resizerStyles: [],
    isPropagate,
    direction: Direction.VERTICAL,
    isStyleInitialized: false,
    isResizeEndScheduled: false,
  });

  useEffect(() => {
    if (haveChildrenDimensionsConfigPropsChangedVertical(children, configs)) {
      setState(previousState => ({
        ...previousState,
        startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
        configs: children.map(getVerticalConfigFromChild),
        activeResizerIndex: -1,
        styles: children.map(toInitialVerticalStyle),
        resizerStyles: [],
        isStyleInitialized: false,
      }));
    }
  }, [children, configs]);

  useEffect(() => {
    if (
      containerRef.current &&
      containerRef.current.getClientRects().length > 0
    ) {
      // This may seem to called a bit often, but
      // reduceIsCollapsedAllChildren can return the current state,
      // so not each call will trigger a render update.
      // The advantage of doing it in this way is that internal state
      // can be accessed.
      setState(state =>
        reduceIsCollapsedAllChildren(state, {
          children,
          containerRef,
          onResizeEnd,
          getValueFromDOMRect: (rect: DOMRect) => rect.height,
        })
      );
    }
  }, [children, containerRef, onResizeEnd, setState]);

  useEffect(() => {
    if (isResizeEndScheduled) {
      // If reduceIsCollapsedAllChildren has updated the state in the above
      // useEffect, we have to call onResizeEnd in the following render update
      // when all the DOM rects have their sizes updated.
      if (onResizeEnd && containerRef.current) {
        onResizeEnd(getContainerState(containerRef.current).boxes);
      }
      setState(state => ({ ...state, isResizeEndScheduled: false }));
    }
  }, [containerRef, isResizeEndScheduled, onResizeEnd, setState]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mouseDownHandler = useCallback(
    getMouseDownHandler({
      containerRef,
      setState,
      getValueFromDOMRect: (rect: DOMRect) => rect.height,
      onResizeEnd,
      onResizeStart,
    }),
    [containerRef, setState]
  );

  const getSetCollapsed = useCallback(
    (index: number) => (isCollapsed: boolean) =>
      setState(state =>
        reduceIsCollapsed(
          updateConfigs(
            state,
            containerRef.current,
            (rect: DOMRect) => rect.height
          ),
          index,
          isCollapsed
        )
      ),
    [setState]
  );

  const isLast = (index: number) => index === children.length - 1;

  return (
    <VerticalContainer onMouseDown={mouseDownHandler} ref={containerRef}>
      {children.flatMap(({ type: Section, props }, index) => [
        <VerticalResizableSectionContainer
          key={`section-${index}`}
          style={styles[index]}
        >
          <Section
            {...props}
            isCollapsed={configs[index].isCollapsed}
            setCollapsed={getSetCollapsed(index)}
          />
        </VerticalResizableSectionContainer>,
        !isLast(index) && (
          <VerticalResizer
            key={`resizer-${index}`}
            $isActive={index === activeResizerIndex}
            style={resizerStyles[index]}
          />
        ),
      ])}
    </VerticalContainer>
  );
};

export { ResizableVertical };
